/**
 * PilotMoatTour — 30-second guided tour surfacing the honest-FinOps moat.
 *
 * Fires ONCE for first-time signed-in users after they've completed the
 * initial cloud-picker onboarding (i.e., onboarding_completed === 'true').
 *
 * Goal: turn the moat features (ROI Pledge, Payback Filter, Zombie Hunter,
 * Pilot Readiness) into the user's first impression — instead of them
 * wandering through 6 mega menus hoping to find them.
 *
 * 4 steps · ~7s each = ~30s total · skippable at any point.
 *
 * Stores `pilot_moat_tour_seen` in localStorage so it never reappears.
 *
 * Visual design:
 *   - Glass-morphism tooltip card anchored next to the Pilot menu trigger
 *   - Arrow pointing at the live nav element
 *   - Dimmed backdrop with a "spotlight" cut-out around the target
 *   - Progress dots + Skip link in the tooltip footer
 *   - All transitions use cubic-bezier(0.22, 1, 0.36, 1) for smoothness
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Handshake, Scale, Skull, ListChecks, ArrowRight, X, Sparkles } from 'lucide-react';

const STEPS = [
  {
    title: 'Meet your honest-FinOps moat',
    body: 'We just added a new menu called "Pilot" — it holds the features no other FinOps tool offers. Quick 30-second tour so you don\'t miss them.',
    icon: Sparkles,
    accent: 'amber',
    target: '[data-testid="nav-pilot"]',
    anchor: 'bottom-center',
  },
  {
    title: 'The ROI Pledge',
    body: 'A binding refund contract. If we don\'t deliver 3× ROI in 90 days, you get a full refund — no questions, no email chains. No other FinOps vendor offers this.',
    icon: Handshake,
    accent: 'emerald',
    target: '[data-testid="nav-pilot"]',
    anchor: 'bottom-center',
    pulseLink: '/roi-pledge',
  },
  {
    title: 'Payback Filter + Zombie Hunter',
    body: 'Every recommendation is gated by engineer-hours to ship. No more "save $50 if you spend 8 hours." Only honest savings show up — the ones that pay back 3× their implementation cost in year one.',
    icon: Scale,
    accent: 'rose',
    target: '[data-testid="nav-pilot"]',
    anchor: 'bottom-center',
    pulseLink: '/admin/payback-filter',
  },
  {
    title: 'Pilot Readiness score',
    body: 'A 12-gate green checklist that climbs from "exploring" to "production-ready" in real time. When all 12 are green, you\'re safe to use CARTIEAI in production.',
    icon: ListChecks,
    accent: 'cyan',
    target: '[data-testid="nav-pilot"]',
    anchor: 'bottom-center',
    pulseLink: '/admin/pilot-activation',
  },
];

const ACCENT_STYLES = {
  amber: { border: 'border-amber-400/40', text: 'text-amber-400', dotActive: 'bg-amber-400', glow: 'shadow-amber-500/20', gradient: 'from-amber-500 to-orange-500' },
  emerald: { border: 'border-emerald-400/40', text: 'text-emerald-400', dotActive: 'bg-emerald-400', glow: 'shadow-emerald-500/20', gradient: 'from-emerald-500 to-teal-500' },
  rose: { border: 'border-rose-400/40', text: 'text-rose-400', dotActive: 'bg-rose-400', glow: 'shadow-rose-500/20', gradient: 'from-rose-500 to-orange-500' },
  cyan: { border: 'border-cyan-400/40', text: 'text-cyan-400', dotActive: 'bg-cyan-400', glow: 'shadow-cyan-500/20', gradient: 'from-cyan-500 to-blue-500' },
};

const PilotMoatTour = () => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();

  // Decide whether to fire the tour on mount
  useEffect(() => {
    const seen = localStorage.getItem('pilot_moat_tour_seen') === '1';
    const onboardingDone = localStorage.getItem('onboarding_completed') === 'true';
    const isAuthed = !!localStorage.getItem('token');
    if (!seen && onboardingDone && isAuthed) {
      // Give the navbar a beat to mount, then arm the tour
      const timer = setTimeout(() => setActive(true), 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Update target rect whenever step changes or window resizes
  const refreshTargetRect = useCallback(() => {
    if (!active) return;
    const sel = STEPS[step].target;
    const el = document.querySelector(sel);
    if (el) {
      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom });
    }
  }, [active, step]);

  useEffect(() => {
    refreshTargetRect();
    window.addEventListener('resize', refreshTargetRect);
    window.addEventListener('scroll', refreshTargetRect, true);
    return () => {
      window.removeEventListener('resize', refreshTargetRect);
      window.removeEventListener('scroll', refreshTargetRect, true);
    };
  }, [refreshTargetRect]);

  const closeTour = useCallback(() => {
    localStorage.setItem('pilot_moat_tour_seen', '1');
    setActive(false);
  }, []);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      closeTour();
    }
  }, [step, closeTour]);

  const back = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  // Escape key closes the tour
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTour();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, back, closeTour]);

  if (!active || !targetRect) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const accent = ACCENT_STYLES[current.accent];

  // Position the tooltip below the target, centered horizontally
  const tooltipWidth = 380;
  const tooltipTop = targetRect.bottom + 16; // 16px below target
  const tooltipLeft = Math.max(
    16,
    Math.min(
      window.innerWidth - tooltipWidth - 16,
      targetRect.left + targetRect.width / 2 - tooltipWidth / 2
    )
  );

  return (
    <>
      {/* Dim backdrop with cut-out spotlight on target */}
      <div
        className="fixed inset-0 z-[9998] pointer-events-auto"
        onClick={closeTour}
        data-testid="pilot-tour-backdrop"
        style={{
          background: `radial-gradient(circle ${Math.max(targetRect.width, targetRect.height) * 1.4}px at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px, rgba(0,0,0,0.20), rgba(0,0,0,0.78) 70%)`,
          transition: 'background 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />

      {/* Pulsing ring on target */}
      <div
        className="fixed z-[9999] pointer-events-none rounded-lg"
        style={{
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
          boxShadow: `0 0 0 3px rgb(251 191 36 / 0.6), 0 0 24px 8px rgb(251 191 36 / 0.35)`,
          animation: 'pilotTourPulse 1.6s ease-in-out infinite',
          transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />

      {/* Arrow connecting tooltip to target */}
      <div
        className="fixed z-[10000] pointer-events-none"
        style={{
          top: targetRect.bottom + 4,
          left: targetRect.left + targetRect.width / 2 - 8,
          width: 0, height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderBottom: '10px solid rgb(251 191 36)',
        }}
      />

      {/* Tooltip card */}
      <div
        className={`fixed z-[10000] w-[380px] rounded-2xl border ${accent.border} bg-slate-900/95 backdrop-blur-xl shadow-2xl ${accent.glow}`}
        style={{
          top: tooltipTop,
          left: tooltipLeft,
          animation: 'pilotTourSlideIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        data-testid={`pilot-tour-card-step-${step + 1}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3">
          <div className="flex items-center gap-3">
            <div className={`inline-flex w-10 h-10 rounded-lg bg-gradient-to-br ${accent.gradient} items-center justify-center shadow-lg ${accent.glow}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className={`text-[10px] font-bold tracking-widest uppercase ${accent.text}`}>
                Step {step + 1} of {STEPS.length}
              </p>
              <h3 className="text-base font-bold text-white leading-tight">
                {current.title}
              </h3>
            </div>
          </div>
          <button
            onClick={closeTour}
            className="text-slate-400 hover:text-white transition-colors p-1 -mr-1 -mt-1"
            aria-label="Close tour"
            data-testid="pilot-tour-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-4">
          <p className="text-sm text-slate-300 leading-relaxed">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-700/50 bg-slate-800 rounded-b-2xl">
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? `w-6 ${accent.dotActive}`
                    : i < step
                    ? 'w-1.5 bg-slate-500'
                    : 'w-1.5 bg-slate-700'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={back}
                className="text-xs text-slate-400 hover:text-white transition-colors px-2"
                data-testid="pilot-tour-back"
              >
                Back
              </button>
            )}
            <button
              onClick={closeTour}
              className="text-xs text-slate-400 hover:text-white transition-colors px-2"
              data-testid="pilot-tour-skip"
            >
              Skip
            </button>
            {current.pulseLink && step === STEPS.length - 1 ? (
              <button
                onClick={() => {
                  closeTour();
                  navigate(current.pulseLink);
                }}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-gradient-to-r ${accent.gradient} text-white text-xs font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all`}
                data-testid="pilot-tour-cta"
              >
                Open it
                <ArrowRight className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={next}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-gradient-to-r ${accent.gradient} text-white text-xs font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all`}
                data-testid="pilot-tour-next"
              >
                {step === STEPS.length - 1 ? 'Got it' : 'Next'}
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pilotTourPulse {
          0%, 100% { box-shadow: 0 0 0 3px rgb(251 191 36 / 0.6), 0 0 24px 8px rgb(251 191 36 / 0.35); }
          50% { box-shadow: 0 0 0 5px rgb(251 191 36 / 0.45), 0 0 32px 14px rgb(251 191 36 / 0.5); }
        }
        @keyframes pilotTourSlideIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default PilotMoatTour;
